/**
 * Per-asset ticket usage — hours and runs beside days (api#978 / 0489, the
 * maintenance agent's data capture; decision D3).
 *
 * D3: an asset's hours DEFAULT to the ticket's hours, and the crew may type a
 * per-asset number instead. So the hours box starts EMPTY with the ticket's
 * hours as its placeholder, and an empty box means "use the default" — the
 * API fills it, never this file. Typing 0 means 0 (the asset sat idle).
 *
 * What goes on the wire, per asset (the API's rules, api#987):
 *
 *   hours box   typed           → hoursUsed: <number>  (0 included)
 *               empty, and the  → hoursUsed: null      (clears the crew's
 *               crew had typed one before                number → default)
 *               empty otherwise → no key               (default / keep)
 *   runs box    typed           → runs: <number>
 *               emptied         → runs: null           (clears)
 *               empty otherwise → no key
 *
 *   days        changed by the crew → daysUsed: <n>
 *               set back to auto, after a saved crew count → daysUsed: null
 *               untouched ("auto") → no key: the API takes the ticket's days
 *               worked (rule C's calculation) on a new row and KEEPS what is
 *               stored on an existing one (operator, 2026-10-09)
 *
 * "No key" is load-bearing: the offline queue spreads this body over the
 * device's copy, and the API keeps a stored crew number when the key is
 * absent. Never send `hoursUsed: 0` for an empty box.
 */

export interface EquipmentUsageRow {
  equipmentId: string;
  name: string;
  type: string;
  wasUsed: boolean;
  daysUsed: number;
  /**
   * 'auto' = the crew has not chosen a day count; the API decides (the ticket's
   * days worked) and the stepper shows "auto". 'crew' = the crew's count.
   */
  daysMode: 'auto' | 'crew';
  /** The SERVER held a crew day count when this row was loaded. */
  savedCrewDays: boolean;
  /** What the crew has typed in the hours box; '' = use the ticket's hours. */
  hoursText: string;
  /** What the crew has typed in the runs box; '' = not recorded. */
  runsText: string;
  /** The SERVER held a crew-entered number when this row was loaded. */
  savedCrewHours: boolean;
  /** The SERVER held a run count when this row was loaded. */
  savedRuns: boolean;
}

/** A row the server returned (GET /employee/my-tickets[/:id]), or a job's asset. */
export function hydrateEquipmentUsageRow(item: any): EquipmentUsageRow {
  const hoursSource = item?.hoursSource ?? item?.hours_source ?? null;
  const hoursUsed = item?.hoursUsed ?? item?.hours_used ?? null;
  const runs = item?.runs ?? null;
  // The server always says hoursSource. The DEVICE's own copy of a ticket
  // (the offline queue spreads the saved body over it) carries hoursUsed with
  // no source — and a number there is one the hand typed, so it is theirs.
  const crewHours = hoursUsed != null && (hoursSource == null || hoursSource === 'crew_entered');
  // Days. The server sends `daysSource` on every row (null for history):
  // crew_entered is the crew's count; ticket_default / defaulted / null are
  // "auto". A row with NO daysSource key is the DEVICE's own copy (the offline
  // queue spreads this editor's body over it) or an API that predates the
  // key — there a present daysUsed is a count this editor sent, which since
  // the change is only ever the crew's, so it reads as crew.
  const src = (item ?? {}) as Record<string, unknown>;
  const daysSource = (src.daysSource ?? src.days_source ?? null) as string | null;
  const sourceKnown = 'daysSource' in src || 'days_source' in src;
  const hasDays = (src.daysUsed ?? src.days_used) != null;
  const crewDays = daysSource === 'crew_entered' || (!sourceKnown && hasDays);
  return {
    equipmentId: item?.equipmentId || item?.equipment_id || item?.id,
    name: item?.name || '',
    type: item?.type || '',
    wasUsed: item?.wasUsed ?? item?.was_used ?? true,
    daysUsed: item?.daysUsed ?? item?.days_used ?? 1,
    daysMode: crewDays ? 'crew' : 'auto',
    savedCrewDays: daysSource === 'crew_entered',
    // A ticket default is NOT shown as a typed value — it is the placeholder.
    hoursText: crewHours ? String(Number(hoursUsed)) : '',
    runsText: runs != null ? String(runs) : '',
    savedCrewHours: crewHours,
    savedRuns: runs != null,
  };
}

/** A fresh row seeded from the job's assigned equipment. */
export function seedEquipmentUsageRow(item: any): EquipmentUsageRow {
  return {
    equipmentId: item?.id,
    name: item?.name || '',
    type: item?.type || '',
    wasUsed: true,
    daysUsed: 1,
    daysMode: 'auto',
    savedCrewDays: false,
    hoursText: '',
    runsText: '',
    savedCrewHours: false,
    savedRuns: false,
  };
}

/** numeric(7,2) on the API — the widest hours value it stores. */
const MAX_HOURS = 99999.99;

/** The first input problem, worded for the hand, or null. Mirrors the API's 400s. */
export function equipmentUsageError(rows: EquipmentUsageRow[]): string | null {
  for (const r of rows) {
    const who = r.name || 'Equipment';
    const h = r.hoursText.trim();
    if (h !== '') {
      const n = Number(h);
      if (!Number.isFinite(n) || n < 0 || n > MAX_HOURS) return `${who}: hours must be a number of 0 or more`;
    }
    const k = r.runsText.trim();
    if (k !== '') {
      const n = Number(k);
      if (!Number.isInteger(n) || n < 1) return `${who}: runs must be a whole number of at least 1`;
    }
  }
  return null;
}

export interface EquipmentUsagePayload {
  equipmentId: string;
  /** Not read by the API. Kept because the offline queue spreads this body
   *  over the device's copy of the ticket, and a reopen in a dead zone names
   *  its rows from it. */
  name: string;
  type: string;
  wasUsed: boolean;
  /** Present only for a crew count (or null to clear one back to auto). */
  daysUsed?: number | null;
  hoursUsed?: number | null;
  runs?: number | null;
}

/** The wire shape. Call equipmentUsageError first; this assumes valid input. */
export function serializeEquipmentUsage(rows: EquipmentUsageRow[]): EquipmentUsagePayload[] {
  return rows.map((r) => {
    const out: EquipmentUsagePayload = {
      equipmentId: r.equipmentId, name: r.name, type: r.type, wasUsed: r.wasUsed,
    };
    if (r.daysMode === 'crew') out.daysUsed = r.daysUsed;
    else if (r.savedCrewDays) out.daysUsed = null;
    const h = r.hoursText.trim();
    if (h !== '') out.hoursUsed = Number(h);
    else if (r.savedCrewHours) out.hoursUsed = null;
    const k = r.runsText.trim();
    if (k !== '') out.runs = Number(k);
    else if (r.savedRuns) out.runs = null;
    return out;
  });
}

/** The stepper's next state: any press makes the count the crew's. */
export function stepDays(r: EquipmentUsageRow, delta: 1 | -1): Pick<EquipmentUsageRow, 'daysUsed' | 'daysMode'> {
  return { daysMode: 'crew', daysUsed: Math.max(1, r.daysUsed + delta) };
}
